import React, { useEffect, useState, useCallback } from 'react';
import Table from '../../components/common/Table';
import Badge from '../../components/common/Badge';
import FilterBar from '../../components/common/FilterBar';
import Modal from '../../components/common/Modal';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import { referralAPI, patientAPI } from '../../services/api';
import { ssLookupAPI, ssPartyAPI } from '../../services/ssApi';
import { PartyFormModal } from '../parties-module/SSParties';

const inp = { width: '100%', padding: '9px 12px', border: '1px solid #ece3e7', borderRadius: 6, fontSize: 13, boxSizing: 'border-box' };
const lbl = { display: 'block', fontSize: 12, fontWeight: 600, color: '#5e4a53', marginBottom: 5 };

const STATUSES   = ['PENDING', 'APPROVED', 'SCHEDULED', 'COMPLETED', 'REJECTED', 'CANCELLED'];
const PRIORITIES = ['LOW', 'MEDIUM', 'HIGH', 'URGENT', 'CRITICAL'];
const SERVICES   = ['WOUND_CARE', 'INJECTIONS', 'ELDERLY_CARE', 'PHYSIOTHERAPY', 'POST_SURGERY_CARE',
                    'MEDICATION_MANAGEMENT', 'VITAL_MONITORING', 'PALLIATIVE_CARE', 'MATERNAL_CARE',
                    'PEDIATRIC_CARE', 'MENTAL_HEALTH', 'REHABILITATION'];

const FILTER_DEF = [
  { key: 'status',   label: 'Status',   type: 'select', options: STATUSES },
  { key: 'priority', label: 'Priority', type: 'select', options: PRIORITIES },
  { key: 'service',  label: 'Service',  type: 'select', options: SERVICES },
];
const BLANK_FILTERS = { status: '', priority: '', service: '' };
const BLANK_FORM    = { patient_id: '', patient_name: '', condition_description: '', required_service: 'WOUND_CARE', priority: 'MEDIUM', notes: '' };

const priColor = p => ({ LOW: '#f1f5f9', MEDIUM: '#fef9c3', HIGH: '#fed7aa', URGENT: '#fecaca', CRITICAL: '#fca5a5' }[p] || '#f1f5f9');
const fmtErr = d => {
  if (!d) return 'An error occurred';
  if (typeof d === 'string') return d;
  if (Array.isArray(d)) return d.map(e => e?.msg ? `${(e.loc || []).slice(-1)[0]}: ${e.msg}` : JSON.stringify(e)).join('; ');
  return d.msg || d.message || JSON.stringify(d);
};

// ── Patient search dropdown ───────────────────────────────────────────────────
// Uses ssPartyAPI (parties-module) with role_code=PATIENT so it works for
// doctors without the global patient.list permission.
function PatientPicker({ value, valueName, onChange, onNew }) {
  const [patients, setPatients] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');

  const load = useCallback(async (q) => {
    setLoading(true);
    try {
      // 1. Fetch patients already in HHMS (scoped to this doctor)
      const hhmsRes = await patientAPI.list({ per_page: 200, ...(q ? { search: q } : {}) })
        .catch(() => ({ data: [] }));
      const hhmsPatients = Array.isArray(hhmsRes?.data) ? hhmsRes.data : [];
      const hhmsIds = new Set(hhmsPatients.map(p => p.party_id));

      // 2. Fetch all PATIENT-role parties from ss* (the doctor's own view)
      const ssRes = await ssPartyAPI.list({ role_code: 'PATIENT', size: 200, ...(q ? { q } : {}) })
        .catch(() => ({ data: [] }));
      const ssPatients = Array.isArray(ssRes?.data) ? ssRes.data : [];

      // 3. For ss* patients that have no HHMS record yet, auto-create them now
      const bridgePromises = ssPatients
        .filter(sp => {
          // Only bridge if not already in HHMS (match by name since no shared ID yet)
          return !hhmsPatients.some(hp =>
            hp.name === ((sp.firstname || '') + (sp.lastname ? ' ' + sp.lastname : '') || sp.name || '').trim()
          );
        })
        .map(async sp => {
          const name = [sp.firstname, sp.lastname].filter(Boolean).join(' ') || sp.name || '';
          if (!name) return null;
          try {
            const created = await patientAPI.create({
              name,
              national_id: sp.national_id || undefined,
              phone: sp.phone || undefined,
              email: sp.email || undefined,
            });
            const pid = created?.data?.party_id || created?.party_id;
            return pid ? { party_id: pid, name } : null;
          } catch { return null; }
        });

      const bridged = (await Promise.all(bridgePromises)).filter(Boolean);

      // 4. Merge: HHMS patients + newly bridged ones
      const all = [...hhmsPatients, ...bridged];
      // dedupe by party_id
      const seen = new Set();
      setPatients(all.filter(p => { if (seen.has(p.party_id)) return false; seen.add(p.party_id); return true; }));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(''); }, [load]);

  // debounce search
  useEffect(() => {
    const t = setTimeout(() => load(search), 300);
    return () => clearTimeout(t);
  }, [search, load]);

  if (value && valueName) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ flex: 1, padding: '9px 12px', border: '1px solid #ece3e7', borderRadius: 6, fontSize: 13, background: '#f9f5f7' }}>
          {valueName}
        </span>
        <button type="button" onClick={() => onChange('', '')}
          style={{ padding: '7px 12px', background: '#f1f5f9', color: '#475569', border: '1px solid #ece3e7', borderRadius: 6, fontSize: 12, cursor: 'pointer' }}>
          Change
        </button>
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ flex: 1, minWidth: 200 }}>
        <input
          style={{ ...inp, marginBottom: 6 }}
          placeholder="Search patient name…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select
          style={{ ...inp }}
          value={value}
          required
          onChange={e => {
            const p = patients.find(x => x.party_id === e.target.value);
            onChange(e.target.value, p ? p.name : '');
          }}
        >
          <option value="">{loading ? 'Loading…' : patients.length === 0 ? 'No patients found' : '— Select patient —'}</option>
          {patients.map(p => (
            <option key={p.party_id} value={p.party_id}>{p.name || p.party_id}</option>
          ))}
        </select>
      </div>
      <button type="button" onClick={onNew}
        style={{ padding: '9px 14px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap', marginTop: 32 }}>
        + New Patient
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

export default function Referrals() {
  const [rows, setRows]       = useState([]);
  const [total, setTotal]     = useState(0);
  const [loading, setLoading] = useState(true);
  const [page, setPage]       = useState(1);
  const PER = 50;
  const [filters, setFilters] = useState(BLANK_FILTERS);

  // lookups for PartyFormModal — same fetch pattern as SSParties.js
  const [lookups, setLookups]           = useState({ partyRoles: [], identTypes: [], contactMedia: [], contactMediaTypes: [], addrTypes: [], relTypes: [] });
  const [lookupsReady, setLookupsReady] = useState(false);

  // create referral state
  const [showCreate, setShowCreate]     = useState(false);
  const [showRegister, setShowRegister] = useState(false);
  const [form, setForm]                 = useState(BLANK_FORM);
  const [saving, setSaving]             = useState(false);
  const [cErr, setCErr]                 = useState('');

  // edit / cancel state
  const [editRow, setEditRow]     = useState(null);
  const [ef, setEf]               = useState({ status: 'PENDING', priority: 'MEDIUM', notes: '' });
  const [eErr, setEErr]           = useState('');
  const [eSaving, setESaving]     = useState(false);
  const [cancelRow, setCancelRow] = useState(null);
  const [cancelLoading, setCancel]= useState(false);

  // Fetch lookups — exact same pattern as SSParties.js so the same arrays flow
  // into PartyFormModal and populate every dropdown correctly.
  useEffect(() => {
    Promise.all([
      ssLookupAPI.partyRoles().catch(()            => ({ data: [] })),
      ssLookupAPI.identifierTypes().catch(()       => ({ data: [] })),
      ssLookupAPI.contactMedia().catch(()          => ({ data: [] })),
      ssLookupAPI.listContactMediaTypes().catch(() => ({ data: [] })),
      ssLookupAPI.addressTypes().catch(()          => ({ data: [] })),
      ssLookupAPI.listRelTypes().catch(()          => ({ data: [] })),
    ]).then(([pr, it, cm, cmt, at, rt]) => {
      setLookups({
        partyRoles:        pr.data  || [],
        identTypes:        it.data  || [],
        contactMedia:      cm.data  || [],
        contactMediaTypes: cmt.data || [],
        addrTypes:         at.data  || [],
        relTypes:          rt.data  || [],
      });
      setLookupsReady(true);
    }).catch(() => setLookupsReady(true));
  }, []);

  const buildParams = useCallback(() => {
    const p = { page, per_page: PER };
    if (filters.status)  p.status       = filters.status;
    if (filters.service) p.service_type = filters.service;
    return p;
  }, [filters, page]);

  const load = useCallback(() => {
    setLoading(true);
    referralAPI.list(buildParams())
      .then(r => { setRows(r.data || []); setTotal(r.meta?.total || 0); })
      .finally(() => setLoading(false));
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { setPage(1); }, [filters]);

  const visible = rows.filter(r => !filters.priority || r.priority === filters.priority);

  const handleCreate = async e => {
    e.preventDefault();
    if (!form.patient_id) { setCErr('Please select a patient.'); return; }
    setSaving(true); setCErr('');
    try {
      await referralAPI.create({
        patient_id:            form.patient_id,
        condition_description: form.condition_description,
        required_service:      form.required_service,
        priority:              form.priority,
        notes:                 form.notes || undefined,
      });
      setShowCreate(false);
      setForm(BLANK_FORM);
      load();
    } catch (err) {
      setCErr(fmtErr(err.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  const openEdit = row => {
    setEditRow(row);
    setEf({ status: row.status, priority: row.priority, notes: row.notes || '' });
    setEErr('');
  };

  const handleEdit = async e => {
    e.preventDefault(); setESaving(true); setEErr('');
    try {
      await referralAPI.update(editRow.referral_id, {
        status:   ef.status,
        priority: ef.priority,
        notes:    ef.notes || undefined,
      });
      setEditRow(null); load();
    } catch (err) {
      setEErr(fmtErr(err.response?.data?.detail));
    } finally {
      setESaving(false);
    }
  };

  const handleCancel = async () => {
    setCancel(true);
    try { await referralAPI.cancel(cancelRow.referral_id); setCancelRow(null); load(); }
    catch { } finally { setCancel(false); }
  };

  const columns = [
    { key: 'referral_date',         label: 'Date',        render: v => v ? new Date(v).toLocaleDateString() : '—' },
    { key: 'patient_id',            label: 'Patient',     render: (_, r) => r.patient_name || r.patient_id?.slice(0, 8) || '—' },
    { key: 'doctor_id',             label: 'Referred by', render: (_, r) => r.doctor_name  || r.doctor_id?.slice(0, 8)  || '—' },
    { key: 'required_service',      label: 'Service',     render: v => (v || '—').replace(/_/g, ' ') },
    { key: 'condition_description', label: 'Condition',   render: v => v?.length > 40 ? v.slice(0, 40) + '…' : (v || '—') },
    { key: 'icd10_code', label: 'ICD-10', render: (_, r) => r.icd10_code
        ? <span title={r.icd10_display || ''} style={{ fontFamily: 'monospace', fontSize: 12 }}>{r.icd10_code}</span>
        : '—' },
    { key: 'priority', label: 'Priority', render: v =>
        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 11, fontWeight: 600, background: priColor(v) }}>{v}</span> },
    { key: 'status', label: 'Status', render: v => <Badge value={v} /> },
    { key: '_actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <button onClick={() => openEdit(row)} style={btnSm('#94335a')} disabled={['CANCELLED','COMPLETED'].includes(row.status)}>✏️</button>
        <button onClick={() => setCancelRow(row)} style={btnSm('#ef4444')} disabled={['CANCELLED','COMPLETED'].includes(row.status)}>✕</button>
      </div>
    )},
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, color: '#2b1b22', margin: 0 }}>Referrals</h1>
          <p style={{ color: '#8b7681', fontSize: 13, marginTop: 4 }}>{total} referrals · showing {visible.length}</p>
        </div>
        <button onClick={() => { setForm(BLANK_FORM); setCErr(''); setShowCreate(true); }}
          style={{ padding: '9px 18px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 8, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
          + New Referral
        </button>
      </div>

      <FilterBar filters={FILTER_DEF} values={filters} onChange={(k, v) => setFilters(f => ({ ...f, [k]: v }))} onClear={() => setFilters(BLANK_FILTERS)} />

      {loading
        ? <div style={{ color: '#8b7681', padding: 20 }}>Loading…</div>
        : <Table columns={columns} data={visible} />}
      <Pagination page={page} total={total} per={PER} onPage={setPage} />

      {/* ── New Referral ─────────────────────────────────────────────────── */}
      {showCreate && (
        <Modal title="New Referral" onClose={() => setShowCreate(false)}>
          {cErr && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{cErr}</div>}
          <form onSubmit={handleCreate}>

            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Patient *</label>
              <PatientPicker
                value={form.patient_id}
                valueName={form.patient_name}
                onChange={(id, name) => setForm(f => ({ ...f, patient_id: id, patient_name: name }))}
                onNew={() => setShowRegister(true)}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={lbl}>Service *</label>
                <select style={inp} value={form.required_service} onChange={e => setForm(f => ({ ...f, required_service: e.target.value }))} required>
                  {SERVICES.map(s => <option key={s} value={s}>{s.replace(/_/g, ' ')}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Priority</label>
                <select style={inp} value={form.priority} onChange={e => setForm(f => ({ ...f, priority: e.target.value }))}>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 14 }}>
              <label style={lbl}>Condition Description *</label>
              <textarea style={{ ...inp, minHeight: 90 }} value={form.condition_description}
                onChange={e => setForm(f => ({ ...f, condition_description: e.target.value }))} required />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={lbl}>Notes</label>
              <textarea style={{ ...inp, minHeight: 70 }} value={form.notes}
                onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
            </div>

            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" disabled={saving}
                style={{ padding: '9px 22px', background: '#8b5cf6', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {saving ? 'Creating…' : 'Create Referral'}
              </button>
              <button type="button" onClick={() => setShowCreate(false)}
                style={{ padding: '9px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {/* ── Register New Patient ─────────────────────────────────────────── */}
      {/* open={true} is required — the ss Modal component returns null if open is falsy */}
      <PartyFormModal
        open={showRegister}
        lookups={lookups}
        defaultRoleCode="PATIENT"
        onClose={() => setShowRegister(false)}
        onDone={async (ssPartyId) => {
          setShowRegister(false);
          if (!ssPartyId) return;
          // ssPartyId is an ss* integer partyid — bridge it to an HHMS Party UUID
          // so the patient appears in the doctor's referral-scoped list after submit.
          try {
            const ssParty = await ssPartyAPI.get(ssPartyId);
            const d = ssParty?.data || ssParty || {};
            const name = d.name
              || [d.firstname, d.lastname].filter(Boolean).join(' ')
              || 'New Patient';
            const hhmsPatient = await patientAPI.create({
              name,
              national_id: d.national_id || d.nationalId || undefined,
              phone: d.phone || undefined,
              email: d.email || undefined,
            });
            const hhmsId = hhmsPatient?.data?.party_id || hhmsPatient?.party_id;
            if (hhmsId) {
              setForm(f => ({ ...f, patient_id: hhmsId, patient_name: name }));
            }
          } catch (err) {
            setCErr('Party created in ss* but could not sync to HHMS: ' + (err?.message || 'unknown error'));
          }
        }}
      />

      {/* ── Edit Referral ─────────────────────────────────────────────────── */}
      {editRow && (
        <Modal title="Update Referral" onClose={() => setEditRow(null)}>
          {eErr && <div style={{ background: '#fee2e2', color: '#991b1b', padding: '8px 12px', borderRadius: 6, marginBottom: 14, fontSize: 13 }}>{eErr}</div>}
          <form onSubmit={handleEdit}>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 14 }}>
              <div>
                <label style={lbl}>Status</label>
                <select style={inp} value={ef.status} onChange={e => setEf(f => ({ ...f, status: e.target.value }))}>
                  {STATUSES.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Priority</label>
                <select style={inp} value={ef.priority} onChange={e => setEf(f => ({ ...f, priority: e.target.value }))}>
                  {PRIORITIES.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
              </div>
            </div>
            <div style={{ marginBottom: 20 }}>
              <label style={lbl}>Notes</label>
              <textarea style={{ ...inp, minHeight: 80 }} value={ef.notes}
                onChange={e => setEf(f => ({ ...f, notes: e.target.value }))} />
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button type="submit" disabled={eSaving}
                style={{ padding: '9px 22px', background: '#10b981', color: '#fff', border: 'none', borderRadius: 7, fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                {eSaving ? 'Saving…' : 'Save'}
              </button>
              <button type="button" onClick={() => setEditRow(null)}
                style={{ padding: '9px 18px', background: '#f1f5f9', color: '#475569', border: 'none', borderRadius: 7, fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </form>
        </Modal>
      )}

      {cancelRow && (
        <ConfirmDialog
          message="Cancel this referral?"
          detail="The patient will need a new referral to receive care."
          onConfirm={handleCancel}
          onCancel={() => setCancelRow(null)}
          loading={cancelLoading}
          confirmLabel="Cancel Referral" />
      )}
    </div>
  );
}

const btnSm = bg => ({ padding: '5px 10px', background: bg, color: '#fff', border: 'none', borderRadius: 6, fontSize: 12, cursor: 'pointer' });

function Pagination({ page, total, per, onPage }) {
  const pages = Math.ceil(total / per);
  if (pages <= 1) return null;
  return (
    <div style={{ display: 'flex', gap: 6, justifyContent: 'center', marginTop: 20 }}>
      <button onClick={() => onPage(p => Math.max(1, p - 1))} disabled={page === 1}
        style={{ padding: '6px 14px', background: '#f1f5f9', border: '1px solid #ece3e7', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>‹</button>
      <span style={{ padding: '6px 12px', fontSize: 13, color: '#475569' }}>Page {page} / {pages}</span>
      <button onClick={() => onPage(p => Math.min(pages, p + 1))} disabled={page === pages}
        style={{ padding: '6px 14px', background: '#f1f5f9', border: '1px solid #ece3e7', borderRadius: 6, fontSize: 13, cursor: 'pointer' }}>›</button>
    </div>
  );
}
